import React, { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { adminApi } from "../../repositories/admin";
import Swal from "sweetalert2";
import { dataTableRowClass, dataTableThClass, dataTableTdClass } from "../ui/DataTable";
import { cn } from "../../DashboardPage/lib/utils";
import { SkeletonTableRows } from "../ui/skeleton";
import Button from "../ui/Button";

interface DspUser {
  id: number;
  email: string;
  fullName: string | null;
  role: string;
  createdAt: string;
}

interface ViewDspUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName: string;
}

const ViewDspUsersModal: React.FC<ViewDspUsersModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  organizationName,
}) => {
  const [users, setUsers] = useState<DspUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 0,
  });

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen, pagination.page]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getDspUsers(organizationId, {
        page: pagination.page,
        limit: pagination.limit,
      });
      setUsers(response.data.data);
      setPagination({
        ...pagination,
        totalItems: response.data.pagination.totalItems,
        totalPages: response.data.pagination.totalPages,
      });
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to fetch DSP users",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.totalPages) {
      setPagination({ ...pagination, page: pagination.page + 1 });
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      setPagination({ ...pagination, page: pagination.page - 1 });
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl p-6 bg-white rounded-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-black">
            Users in {organizationName}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            // Skeleton rows shaped like the users table below, so the modal
            // keeps its height rather than collapsing around a spinner.
            <div className="overflow-x-auto" aria-busy="true">
              <table className="min-w-full">
                <tbody>
                  <SkeletonTableRows rows={5} columns={5} />
                </tbody>
              </table>
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center text-neutral-500">
              <h3 className="mb-2 text-lg font-semibold">No Users Found</h3>
              <p className="text-sm">This organization has no users yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full bg-white">
                <thead className="border-b border-neutral-200 bg-primary-50">
                  <tr>
                    <th className={dataTableThClass}>ID</th>
                    <th className={dataTableThClass}>Email</th>
                    <th className={dataTableThClass}>Full Name</th>
                    <th className={dataTableThClass}>Role</th>
                    <th className={dataTableThClass}>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr
                      key={user.id}
                      className={dataTableRowClass}
                    >
                      <td className={dataTableTdClass}>{user.id}</td>
                      <td className={dataTableTdClass}>{user.email}</td>
                      <td className={dataTableTdClass}>
                        {user.fullName || "N/A"}
                      </td>
                      <td className={dataTableTdClass}>
                        <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium border rounded-full text-primary-800 bg-primary-50 border-primary-200">
                          {user.role.toUpperCase()}
                        </span>
                      </td>
                      <td className={cn(dataTableTdClass,"whitespace-nowrap")}>
                        {formatDate(user.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && users.length > 0 && (
          <div className="flex items-center justify-between pt-4 mt-4 border-t">
            <div className="text-sm text-neutral-600">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
              {Math.min(pagination.page * pagination.limit, pagination.totalItems)} of{" "}
              {pagination.totalItems} users
            </div>
            <div className="flex gap-2">
              <Button
                variant="neutral"
                onClick={handlePrevPage}
                disabled={pagination.page === 1}
              >
                Previous
              </Button>
              <Button
                variant="neutral"
                onClick={handleNextPage}
                disabled={pagination.page >= pagination.totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewDspUsersModal;
